import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMockAuth } from '@/hooks/useMockAuth';
import { useTheme } from '@/contexts/ThemeContext';

const Dashboard = () => {
  const { user } = useMockAuth();
  const { theme } = useTheme();

  if (!user) {
    return (
      <div style={{
        minHeight: '89.7vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme === 'dark' ? '#0a0a0f' : '#f9fafb'
      }}>
        <p style={{
          color: theme === 'dark' ? '#9ca3af' : '#6b7280',
          fontSize: '1.125rem'
        }}>
          Please log in to view your dashboard
        </p>
      </div>
    );
  }

  const colors = {
    dark: {
      bg: '#0a0a0f',
      cardBg: 'linear-gradient(135deg, rgba(26, 26, 46, 0.6) 0%, rgba(22, 33, 62, 0.6) 100%)',
      cardBorder: 'rgba(102, 126, 234, 0.2)',
      text: '#ffffff',
      textMuted: '#9ca3af',
      hover: 'rgba(102, 126, 234, 0.1)'
    },
    light: {
      bg: '#f9fafb',
      cardBg: '#ffffff',
      cardBorder: '#e5e7eb',
      text: '#111827',
      textMuted: '#6b7280',
      hover: '#f3f4f6'
    }
  };

  const c = colors[theme];

  const formatDate = (date: string | undefined) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return '#dc2626';
      case 'creator':
        return '#7c3aed';
      case 'user':
        return '#2563eb';
      default:
        return '#6b7280';
    }
  };

  return (
    <div style={{ backgroundColor: c.bg, minHeight: '89.7vh' }}>
      {/* Hero Header */}
      <div style={{
        background: theme === 'dark'
          ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)'
          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '4rem 2rem 3rem',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h1 style={{
            fontSize: '3rem',
            fontWeight: 'bold',
            color: 'white',
            marginBottom: '0.5rem'
          }}>
            Welcome back, {user.email.split('@')[0]}!
          </h1>
          <p style={{
            fontSize: '1.125rem',
            color: 'rgba(255, 255, 255, 0.9)'
          }}>
            Manage your account, purchases, and settings from your dashboard
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '2rem',
          marginBottom: '3rem'
        }}>
          {/* Stats Cards */}
          <StatCard
            theme={theme}
            icon="🎵"
            label="Products Owned"
            value="12"
            gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
          />
          <StatCard
            theme={theme}
            icon="💾"
            label="Presets Saved"
            value="48"
            gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
          />
          <StatCard
            theme={theme}
            icon="⭐"
            label="Favorites"
            value="23"
            gradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
          />
        </div>

        {/* Account Information */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
          gap: '2rem'
        }}>
          {/* Account Details */}
          <Section title="Account Information" icon="👤" theme={theme}>
            <InfoRow label="Email" value={user.email} theme={theme} />
            <InfoRow label="User ID" value={user.userId.slice(0, 8) + '...'} theme={theme} />
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '0.75rem',
              backgroundColor: theme === 'dark' ? 'rgba(15, 15, 20, 0.6)' : '#f9fafb',
              borderRadius: '0.5rem'
            }}>
              <span style={{
                fontSize: '0.875rem',
                color: c.textMuted,
                fontWeight: '500'
              }}>
                Role
              </span>
              <span style={{
                padding: '0.25rem 0.75rem',
                borderRadius: '9999px',
                fontSize: '0.75rem',
                fontWeight: '600',
                backgroundColor: getRoleColor(user.role),
                color: 'white',
                textTransform: 'uppercase'
              }}>
                {user.role}
              </span>
            </div>
            <InfoRow label="Member Since" value={formatDate(user.createdAt)} theme={theme} />
            <InfoRow label="Last Login" value={formatDate(user.lastLoginAt)} theme={theme} />
          </Section>

          {/* Quick Actions */}
          <Section title="Quick Actions" icon="⚡" theme={theme}>
            <ActionCard
              to="/profile"
              icon="👤"
              title="View Profile"
              description="Manage your personal information and settings"
              theme={theme}
            />
            <ActionCard
              to="/marketplace"
              icon="🛒"
              title="Browse Marketplace"
              description="Discover new effects, samples, and presets"
              theme={theme}
            />
            <ActionCard
              to="#"
              icon="💾"
              title="My Library"
              description="Access your purchased content"
              theme={theme}
              disabled
            />
          </Section>
        </div>

        {/* Recent Activity */}
        <div style={{ marginTop: '3rem' }}>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: '600',
            color: c.text,
            marginBottom: '1.5rem'
          }}>
            Recent Activity
          </h2>
          <div style={{
            background: c.cardBg,
            border: `1px solid ${c.cardBorder}`,
            borderRadius: '1rem',
            padding: '2rem',
            backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none'
          }}>
            <p style={{ color: c.textMuted, textAlign: 'center' }}>
              No recent activity to display
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

interface StatCardProps {
  theme: 'dark' | 'light';
  icon: string;
  label: string;
  value: string;
  gradient: string;
}

function StatCard({ theme, icon, label, value, gradient }: StatCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        background: theme === 'dark'
          ? 'linear-gradient(135deg, rgba(26, 26, 46, 0.6) 0%, rgba(22, 33, 62, 0.6) 100%)'
          : '#ffffff',
        border: `1px solid ${theme === 'dark' ? 'rgba(102, 126, 234, 0.2)' : '#e5e7eb'}`,
        borderRadius: '1rem',
        padding: '2rem',
        backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none',
        boxShadow: isHovered
          ? '0 12px 24px rgba(0,0,0,0.15)'
          : theme === 'dark' ? '0 4px 6px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
        transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
        transition: 'all 0.3s ease',
        cursor: 'pointer'
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '1rem'
      }}>
        <div style={{
          width: '60px',
          height: '60px',
          borderRadius: '12px',
          background: gradient,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '2rem'
        }}>
          {icon}
        </div>
        <div>
          <p style={{
            fontSize: '0.875rem',
            color: theme === 'dark' ? '#9ca3af' : '#6b7280',
            marginBottom: '0.25rem'
          }}>
            {label}
          </p>
          <p style={{
            fontSize: '2rem',
            fontWeight: 'bold',
            color: theme === 'dark' ? '#ffffff' : '#111827',
            margin: 0
          }}>
            {value}
          </p>
        </div>
      </div>
    </div>
  );
}

interface SectionProps {
  title: string;
  icon: string;
  children: React.ReactNode;
  theme: 'dark' | 'light';
}

function Section({ title, icon, children, theme }: SectionProps) {
  return (
    <div style={{
      background: theme === 'dark'
        ? 'linear-gradient(135deg, rgba(26, 26, 46, 0.6) 0%, rgba(22, 33, 62, 0.6) 100%)'
        : '#ffffff',
      borderRadius: '1rem',
      padding: '2rem',
      border: `1px solid ${theme === 'dark' ? 'rgba(102, 126, 234, 0.2)' : '#e5e7eb'}`,
      backdropFilter: theme === 'dark' ? 'blur(10px)' : 'none',
      boxShadow: theme === 'dark' ? '0 4px 6px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        marginBottom: '1.5rem'
      }}>
        <span style={{ fontSize: '1.5rem' }}>{icon}</span>
        <h2 style={{
          fontSize: '1.5rem',
          fontWeight: '600',
          color: theme === 'dark' ? '#ffffff' : '#111827',
          margin: 0
        }}>
          {title}
        </h2>
      </div>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        {children}
      </div>
    </div>
  );
}

interface InfoRowProps {
  label: string;
  value: string;
  theme: 'dark' | 'light';
}

function InfoRow({ label, value, theme }: InfoRowProps) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '0.75rem',
      backgroundColor: theme === 'dark' ? 'rgba(15, 15, 20, 0.6)' : '#f9fafb',
      borderRadius: '0.5rem'
    }}>
      <span style={{
        fontSize: '0.875rem',
        color: theme === 'dark' ? '#9ca3af' : '#6b7280',
        fontWeight: '500'
      }}>
        {label}
      </span>
      <span style={{
        fontSize: '0.875rem',
        color: theme === 'dark' ? '#ffffff' : '#111827',
        fontWeight: '600'
      }}>
        {value}
      </span>
    </div>
  );
}

interface ActionCardProps {
  to: string;
  icon: string;
  title: string;
  description: string;
  theme: 'dark' | 'light';
  disabled?: boolean;
}

function ActionCard({ to, icon, title, description, theme, disabled }: ActionCardProps) {
  const [isHovered, setIsHovered] = useState(false);

  const content = (
    <div
      onMouseEnter={() => !disabled && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        padding: '1rem',
        backgroundColor: isHovered && !disabled
          ? (theme === 'dark' ? 'rgba(102, 126, 234, 0.1)' : '#f3f4f6')
          : (theme === 'dark' ? 'rgba(15, 15, 20, 0.6)' : '#f9fafb'),
        border: `1px solid ${theme === 'dark' ? 'rgba(102, 126, 234, 0.2)' : '#e5e7eb'}`,
        borderRadius: '0.5rem',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s',
        opacity: disabled ? 0.5 : 1
      }}
    >
      <span style={{ fontSize: '1.5rem' }}>{icon}</span>
      <div style={{ flex: 1 }}>
        <p style={{
          fontSize: '0.875rem',
          fontWeight: '600',
          color: theme === 'dark' ? '#ffffff' : '#111827',
          marginBottom: '0.25rem'
        }}>
          {title}
        </p>
        <p style={{
          fontSize: '0.75rem',
          color: theme === 'dark' ? '#9ca3af' : '#6b7280',
          margin: 0
        }}>
          {description}
        </p>
      </div>
      <span style={{
        fontSize: '1.25rem',
        color: theme === 'dark' ? '#9ca3af' : '#6b7280',
        transform: isHovered && !disabled ? 'translateX(4px)' : 'translateX(0)',
        transition: 'transform 0.2s'
      }}>
        →
      </span>
    </div>
  );

  if (disabled) {
    return content;
  }

  return <Link to={to} style={{ textDecoration: 'none' }}>{content}</Link>;
}

export default Dashboard;
